import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Sparkles, Clock } from 'lucide-react';
import { blogPosts } from '../mock';
import SEO from '../components/SEO';

const Blog = () => {
  return (
    <div className="bg-white text-gray-900 min-h-screen">
      <SEO 
        title="Blog - IlinkTrading | Tech Insights & Industry Trends"
        description="Stay updated with the latest insights on web development, blockchain, mobile apps, and digital marketing. Expert articles and industry trends."
        keywords="tech blog, web development blog, blockchain insights, digital marketing tips, industry trends, technology articles"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 lg:px-8 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" />
        <div className="absolute top-40 left-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md mb-6">
            <Sparkles className="text-purple-600" size={18} />
            <span className="text-sm font-semibold text-gray-700">Latest Insights</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Expert Insights &
            </span>
            <br />
            <span className="text-gray-900">Industry Trends</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay ahead with the latest in technology, innovation, and digital transformation
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => {
              const gradients = [
                { border: 'border-blue-200', gradient: 'from-blue-500 to-cyan-500', badge: 'bg-blue-100 text-blue-700' },
                { border: 'border-purple-200', gradient: 'from-purple-500 to-pink-500', badge: 'bg-purple-100 text-purple-700' },
                { border: 'border-orange-200', gradient: 'from-orange-500 to-red-500', badge: 'bg-orange-100 text-orange-700' },
              ];
              const color = gradients[index % gradients.length];

              return (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="block"
                >
                  <article
                    className={`group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 ${color.border} hover:-translate-y-2 h-full`}
                  >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-56 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className={`absolute top-4 right-4 px-4 py-2 ${color.badge} rounded-full text-xs font-bold uppercase tracking-wider shadow-lg`}>
                      {post.category}
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-purple-600 group-hover:to-pink-600 transition-all line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2">{post.excerpt}</p>

                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                      <User size={14} />
                      <span>{post.author}</span>
                    </div>

                    <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${color.gradient} text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-300 group-hover:gap-3`}>
                      Read More
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </article>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-grid-white/10" />
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Subscribe to Our Newsletter
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Get the latest insights, trends, and updates delivered straight to your inbox
              </p>
              <form className="max-w-xl mx-auto flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-6 py-4 rounded-xl bg-white text-gray-900 focus:outline-none focus:ring-4 focus:ring-white/30 shadow-lg"
                  required
                />
                <button
                  type="submit"
                  className="bg-white text-purple-600 px-8 py-4 rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Subscribe
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
